import { FC } from 'react'

import { ReportMonthlyWebPage } from 'commons/types/DTO/bank/reportWebPage'
import { DownloadLink, Tooltip } from 'ui/components'
import { ButtonGroup } from 'ui/components/ButtonGroup'
import { Button } from 'ui/components/Buttons'
import { IconSize } from 'ui/components/Icon'
import { Info } from 'ui/components/Info'
import { Link } from 'ui/components/Link'
import { PageSection, PageSectionFace, PageSectionPrimary } from 'ui/components/Page'
import { TextMask } from 'ui/components/TextMask'

import { Warnings } from '../Warnings'
import {
  DepositsFromSalesTable,
  OtherBankingActivityTable,
  SalesReportTable,
  SalesToDepositComparisonTable
} from './components'

import * as SC from './styles'

export const BankAccountData: FC<{ webPage: ReportMonthlyWebPage }> = ({ webPage }) => {
  const { bankAccounts, monthlyAnalyticsId } = webPage

  return (
    <>
      {bankAccounts.map((bankAccount, index) => {
        const title = (
          <span style={{ display: 'flex', columnGap: '10px' }}>
            <span>Bank Account #{index + 1}</span>
            <TextMask
              isBankAccount
              text={bankAccount.bankAccountNumber || '---'}
              link={bankAccount.bankAccountId ? `/bank-accounts/${bankAccount.bankAccountId}` : undefined}
              iconSize={IconSize.XS}
            />
          </span>
        )
        return (
          <PageSectionPrimary key={index} face="solid" expanded foldable title={title} anchor={`bank-account-${index}`}>
            {bankAccount.showWarnings !== undefined && <Warnings warnings={bankAccount.showWarnings} />}
            <SC.SalesReportTableWrapper>
              {bankAccount.showSalesRetail && (
                <SalesReportTable
                  salesInfo={bankAccount.showSalesRetail}
                  monthlyAnalyticsId={monthlyAnalyticsId}
                  bankAccountId={bankAccount.bankAccountId}
                  title={'Retail Sales'}
                />
              )}
              {bankAccount.showSalesWholesale && (
                <SalesReportTable
                  salesInfo={bankAccount.showSalesWholesale}
                  monthlyAnalyticsId={monthlyAnalyticsId}
                  bankAccountId={bankAccount.bankAccountId}
                  title={'Wholesale Sales'}
                />
              )}
            </SC.SalesReportTableWrapper>
            <PageSection
              title={'Bank Account Deposits'}
              face={PageSectionFace.SECONDARY}
              actions={
                <ButtonGroup margin="small">
                  {bankAccount.enableDepositsExport && (
                    <DownloadLink
                      direct
                      parametersForm={{
                        monthlyAnalyticsId: monthlyAnalyticsId,
                        bankAccountId: bankAccount.bankAccountId
                      }}
                      handleError={() => ({})}
                      baseUrl="/bank/v1/deposits-data/core-source-download-xlsx"
                      name={'Download Deposit Transactions'}
                    >
                      Download Deposit Transactions
                    </DownloadLink>
                  )}
                  {bankAccount.deposits.enableEdit === false &&
                    bankAccount.deposits.showEditButtonTooltip !== undefined && (
                      <Tooltip
                        id={`deposits-edit-${bankAccount.bankAccountNumber}`}
                        type="dark"
                        content={bankAccount.deposits.showEditButtonTooltip}
                      >
                        <Button face="secondary" disabled>
                          Edit
                        </Button>
                      </Tooltip>
                    )}
                  {bankAccount.deposits.enableEdit && (
                    <Link
                      to="/mr/$id/$bankAccountId/deposits-edit"
                      params={{
                        id: monthlyAnalyticsId.toString(),
                        bankAccountId: bankAccount.bankAccountId.toString()
                      }}
                    >
                      <Button face="tertiary">Edit</Button>
                    </Link>
                  )}
                </ButtonGroup>
              }
            >
              {bankAccount.deposits.depositsFromSales !== undefined && (
                <DepositsFromSalesTable depositsFromSales={bankAccount.deposits.depositsFromSales} />
              )}
              {bankAccount?.deposits.showOtherActivity && (
                <OtherBankingActivityTable otherActivityInfo={bankAccount.deposits.showOtherActivity} />
              )}
              {bankAccount.deposits.notes !== undefined && <Info label="Notes">{bankAccount.deposits.notes}</Info>}
            </PageSection>
            {bankAccount.comparison !== undefined && (
              <PageSection title={'Comparison'} face={PageSectionFace.SECONDARY}>
                <SalesToDepositComparisonTable comparisonInfo={bankAccount.comparison} />
              </PageSection>
            )}
          </PageSectionPrimary>
        )
      })}
    </>
  )
}
